import { Outlet, useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import NavbarV3 from '../components/layout/NavbarV3'
import FooterV3 from '../components/layout/FooterV3'
import BookingModal from '../components/booking/BookingModal'
import CallSalonModal from '../components/booking/CallSalonModal'
import CartDrawer from '../components/shop/CartDrawer'
import NotificationBanners from '../components/system/NotificationBanners'
import CookieConsentBanner from '../components/system/CookieConsentBanner'
import ChatLauncher from '../components/system/ChatLauncher'
import BackToTop from '../components/system/BackToTop'
import MaintenancePage from '../components/system/MaintenancePage'
import ConciergeWidget from '../components/concierge/ConciergeWidget'
import { useBootstrap } from '../lib/useBootstrap'
import { isFeatureEnabled, isSiteMaintenance, getMaintenanceMessage } from '../lib/featureFlags'
import { getConciergeConfig } from '../config/concierge'

// ConciergeChat statically pulls in react-markdown + remark-gfm (the markdown
// parser, ~100KB+ gzip). It's a floating widget that renders CLOSED on first
// paint, so deferring it keeps the parser out of the main bundle and off the
// critical path — it loads only when the chat is actually opened. Suspense
// fallback is null because the prerendered HTML has no open-chat markup to match.
const ConciergeChat = lazy(() => import('../components/concierge/ConciergeChat'))

// ThemeTuner is a dev/designer-only tool. We hoist its enable-guard here so the
// whole module (its lucide icons + font logic) is never even imported when
// disabled — it's tree-shaken out of the main chunk and ships as a separate
// lazy chunk only when enabled.
//
// Gate ONLY on the VITE_THEME_TUNER_ENABLED env flag — NOT import.meta.env.DEV.
// Reason: the prerender (scripts/prerender.mjs) SSRs the app through a Vite dev
// server where import.meta.env.DEV is true, while the shipped client runs the
// prod build where it's false. Gating on DEV would render the tuner into the
// prerendered HTML but not on the client → hydration mismatch (React #418).
// A VITE_-prefixed env var is statically inlined identically in BOTH passes, so
// server and client always agree. For local dev, set the flag in .env.local.
const themeTunerEnabled =
  (import.meta.env.VITE_THEME_TUNER_ENABLED as string | undefined)?.toLowerCase() === 'true'
const ThemeTuner = themeTunerEnabled
  ? lazy(() => import('../components/dev/ThemeTuner'))
  : null

const pagePath = (pathname: string) => pathname.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/'

export default function RootLayout() {
  const location = useLocation()
  const prevPage = useRef(pagePath(location.pathname))
  const conciergeConfig = getConciergeConfig()
  // Text concierge (ConciergeChat) talks to /api/chat — it needs no ElevenLabs
  // agent id, only a text-capable mode. The voice/multimodal ElevenLabs widget
  // (ConciergeWidget) additionally requires an agent id.
  const conciergeChatOn =
    conciergeConfig.enabled &&
    (conciergeConfig.mode === 'chat_only' || conciergeConfig.mode === 'multimodal')
  const conciergeVoiceOn = conciergeConfig.enabled && conciergeConfig.agentId !== ''
  const { ready } = useBootstrap()

  // 150ms grace window before showing any boot UI: most users on warm
  // cache see no flash at all. After 150ms, show a subtle spinner. Without
  // this gate, every FeatureGate / isFeatureEnabled call runs against an
  // empty runtime config and falls through to env or defaults, so a flag
  // whose env disagrees with runtime (e.g. env says SHOP=false but
  // runtime says SHOP=true) renders the wrong state and never re-renders
  // when runtime arrives — visible as "refresh shows coming-soon, click
  // shows real page". Pattern from [[section-skeletons]].
  const [showSplash, setShowSplash] = useState(false)
  useEffect(() => {
    if (ready) return
    const t = setTimeout(() => setShowSplash(true), 150)
    return () => clearTimeout(t)
  }, [ready])

  useEffect(() => {
    const current = pagePath(location.pathname)
    if (current !== prevPage.current) {
      window.scrollTo(0, 0)
      prevPage.current = current
    }
  }, [location.pathname])

  if (!ready) {
    // Pre-150ms: invisible ivory wash so we don't flash white. Post-150ms:
    // a subtle spinner so a slow boot doesn't feel frozen. Either way,
    // routes (FeatureGate, isFeatureEnabled, isSiteMaintenance) never run
    // until runtime config has either loaded or failed (loaded=true is set
    // in loadRuntimeConfig's `finally`, so failure can't hang the app).
    return showSplash ? (
      <div
        className="min-h-screen flex items-center justify-center bg-ivory"
        aria-hidden
      >
        <div className="h-10 w-10 rounded-full border-2 border-mocha/15 border-t-mocha/40 animate-spin" />
      </div>
    ) : (
      <div className="min-h-screen bg-ivory" aria-hidden />
    )
  }

  if (isSiteMaintenance()) {
    return (
      <div className="min-h-screen flex flex-col bg-ivory">
        <MaintenancePage message={getMaintenanceMessage()} />
      </div>
    )
  }

  return (
    // `w-full overflow-x-clip` here (not just on <html>) is the iOS-Safari fix.
    // Safari does NOT reliably honour `overflow-x: clip` on the <html> element,
    // so a few px of sub-viewport overflow still let the page swipe-scroll right
    // and exposed a strip of ivory on the right edge (while the fixed chat
    // button stayed pinned, making it obvious). Putting the clip on this real
    // positioned block box — which is already `relative` — is the containment
    // Safari does honour. `clip` (not `hidden`) avoids creating a scroll
    // container, so the sticky navbar and full-height footer are unaffected.
    <div className="min-h-screen flex flex-col relative w-full overflow-x-clip bg-ivory">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[1000] focus:px-4 focus:py-2 focus:bg-mocha focus:text-ivory focus:rounded focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-gold"
      >
        Skip to content
      </a>
      <NotificationBanners />
      <NavbarV3 />
      <AnimatePresence mode="popLayout">
        <motion.main
          id="main-content"
          tabIndex={-1}
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
          className="flex-1 focus:outline-none"
        >
          <Outlet />
        </motion.main>
      </AnimatePresence>
      <FooterV3 />
      <BookingModal />
      <CallSalonModal />
      <CartDrawer />
      {/* CHAT flag is the universal on/off for any chat widget. When false,
          nothing chat-related renders. When true, precedence is:
            1. ConciergeChat  — text concierge on /api/chat (chat_only / multimodal)
            2. ConciergeWidget — ElevenLabs voice concierge (voice_only, needs agent id)
            3. ChatLauncher    — legacy WhatsApp/Tidio/etc.
          ConciergeChat takes precedence over the legacy launcher whenever the
          concierge is enabled in a text-capable mode. */}
      {isFeatureEnabled('CHAT') &&
        (conciergeChatOn ? (
          <Suspense fallback={null}>
            <ConciergeChat />
          </Suspense>
        ) : conciergeVoiceOn ? (
          <ConciergeWidget />
        ) : (
          <ChatLauncher />
        ))}
      <BackToTop />
      <CookieConsentBanner />
      {ThemeTuner && (
        <Suspense fallback={null}>
          <ThemeTuner />
        </Suspense>
      )}
    </div>
  )
}
